import React, { useEffect, useRef, useState } from 'react';
import './MapComponent.css';
import PointWithRadius from './PointWithRadius';
import axiosInstance, { BASE_API_URL } from '../../../utils/router/axiosInstance';
import { getMapRenderSize } from '../../../utils/mapCoordinates';
import { getMapCalibration } from '../../../utils/mapCalibration';
import { logger } from '../../../utils/logger';

const MapComponentSingle = ({ employee, floor, building_id }) => {
  const containerRef = useRef(null);
  const [mapImage, setMapImage] = useState('');
  const [floorSize, setFloorSize] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [calibration, setCalibration] = useState(getMapCalibration({ buildingId: building_id, floor }));
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const updateContainerWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateContainerWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateContainerWidth);

      return () => window.removeEventListener('resize', updateContainerWidth);
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const [entry] = entries;

      if (!entry) {
        return;
      }

      setContainerWidth(entry.contentRect.width);
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  const mapSize = getMapRenderSize({
    sourceWidth: imageSize.width,
    sourceHeight: imageSize.height,
    containerWidth,
  });

  useEffect(() => {
    const handleCalibrationUpdate = (event) => {
      const updatedBuildingId = String(event.detail?.buildingId);
      const updatedFloor = Number(event.detail?.floor);

      if (updatedBuildingId !== String(building_id) || updatedFloor !== Number(floor)) {
        return;
      }

      setCalibration(
        getMapCalibration({
          buildingId: building_id,
          floor,
          fallbackPlanWidth: floorSize.x,
          fallbackPlanHeight: floorSize.y,
        }),
      );
    };

    window.addEventListener('map-calibration-updated', handleCalibrationUpdate);

    return () => window.removeEventListener('map-calibration-updated', handleCalibrationUpdate);
  }, [building_id, floor, floorSize.x, floorSize.y]);

  useEffect(() => {
    const fetchData = async () => {
      if (localStorage.getItem('current_floor_amount') < localStorage.getItem('selectedFloor')) {
        localStorage.setItem('selectedFloor', 1);
      }
      try {
        const mapResponse = await axiosInstance.get(`website/map/${building_id}?floor=${floor}`);
        const floorData = mapResponse.data[0];

        if (!floorData || floorData.floor_plan === '404') {
          logger.warn('Карта этажа не найдена');
          setMapImage('');
          setImageSize({ width: 0, height: 0 });
          return;
        }

        const nextFloorSize = {
          x: Number(floorData.floor_size?.x) || 0,
          y: Number(floorData.floor_size?.y) || 0,
        };

        setMapImage(floorData.floor_plan);
        setImageSize({ width: 0, height: 0 });
        setFloorSize(nextFloorSize);
        setCalibration(
          getMapCalibration({
            buildingId: building_id,
            floor,
            fallbackPlanWidth: nextFloorSize.x,
            fallbackPlanHeight: nextFloorSize.y,
          }),
        );
      } catch (error) {
        setMapImage('');
        setImageSize({ width: 0, height: 0 });
        setFloorSize({ x: 0, y: 0 });
        logger.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [building_id, floor]);

  return (
    <div className="mapContainerSingle" ref={containerRef}>
      <div className="mapImageContainer" style={{ width: `${mapSize.width}px`, height: `${mapSize.height}px` }}>
        {mapImage && (
          <img
            src={`${BASE_API_URL}${mapImage}`}
            className="mapImage"
            alt="floorPlan"
            onLoad={(event) => {
              setImageSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              });
            }}
          />
        )}
        {employee && (
          <PointWithRadius
            planWidth={calibration.planWidth}
            planHeight={calibration.planHeight}
            calibration={calibration}
            entityType="history"
            mapWidth={mapSize.width}
            mapHeight={mapSize.height}
            x={employee.x}
            y={employee.y}
            radius={employee.r}
          />
        )}
      </div>
    </div>
  );
};

export default MapComponentSingle;
